import { queryClient } from 'queryClient';
import { UseQueryOptions, QueryKey } from 'react-query';

/**
 * Generic Query class to encapsulate common react-query plumbing into one place.
 * Example usage:
 * const namespacesListQuery = Query.create<ListNamespacesRequest, ListNamespacesResponse>(
 *   (options) => ['ListNamespacesRequest', String(options.pageSize ?? 0), options.filter ?? ''],
 *   getNamespaces
 * );
 *
 * // Prefetching
 * await namespacesListQuery.prefetch({ pageSize: 10, filter: '' });
 *
 * // Invalidating
 * await namespacesListQuery.invalidate({ pageSize: 10, filter: '' });
 *
 * // Using in useQuery
 * const { data, isLoading } = useQuery(
 *   namespacesListQuery.getUseQueryOptions({ pageSize: 10, filter: '' })
 * );
 *
 * // Not wrapping useQuery allows more flexibility with react-query hooks
 * useQueries({
 *  queries: [
 *    namespacesListQuery.getUseQueryOptions({ pageSize: 10, filter: '' }),
 *    anotherQuery.getUseQueryOptions({ ... }),
 *  ]
 * });
 */
export class Query<Req, Res> {
  private getQueryKey: (options: Req) => QueryKey;
  private fetchFn: (options: Req) => Promise<Res>;

  static create<Req, Res>(
    getQueryKey: (options: Req) => QueryKey,
    fetchFn: (options: Req) => Promise<Res>
  ) {
    return new Query<Req, Res>(getQueryKey, fetchFn);
  }

  constructor(
    getQueryKey: (options: Req) => QueryKey,
    fetchFn: (options: Req) => Promise<Res>
  ) {
    this.getQueryKey = getQueryKey;
    this.fetchFn = fetchFn;
  }

  prefetch(options: Req) {
    return queryClient.prefetchQuery(this.getQueryKey(options), () =>
      this.fetchFn(options)
    );
  }

  invalidate(options: Req) {
    return queryClient.invalidateQueries({ queryKey: this.getQueryKey(options) });
  }

  getUseQueryOptions(
    options: Req,
    queryOptions?: UseQueryOptions<Res, unknown, Res, QueryKey>
  ): UseQueryOptions<Res, unknown, Res, QueryKey> {
    return {
      queryKey: this.getQueryKey(options),
      queryFn: () => this.fetchFn(options),
      ...queryOptions,
    };
  }
}
